import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { collectionsEndpoints, datasourceEndpoints } from '@/api/endpoints';
import { API as collectionsAPI } from '@/hooks/useCollections';
import { API as datasourcesAPI } from '@/hooks/useDatasources';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { Collections, DatasourceType, WebsiteConfig } from '@/shared/types/collections';
import { WebWidgetConfig } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { normalizeWebsiteUrl } from './page-url';

/**
 * A best guess at the customer's website, so the author never types their own
 * domain.
 *
 * Two sources, and the order is by authority rather than by cost:
 *
 *  1. **The integration's trusted domains.** Literally "where this webchat is
 *     allowed to appear", so when it is set it is the right answer. Measured on
 *     production, though, only 56 of 6,473 web integrations set it — under 1% —
 *     so on its own this would almost never fire.
 *  2. **A website datasource.** If the account crawled their site for a
 *     knowledge base, the crawl's first seed is their site. 946 of the 1,822
 *     accounts that have a webchat also have one of these — about half.
 *
 * Only ever a *suggestion*: the caller fills an empty field with it and never
 * overwrites what the author typed or what was saved.
 */

/** The collections list already counts website datasources per collection. */
const collectionWithWebsite = (data: Collections | undefined): string | undefined =>
  data?.collections?.find((collection) => (collection.content?.website ?? 0) > 0)?.collection_id;

const originOfFirstSeed = (config: WebsiteConfig): string => {
  // A seed is the root of a crawl and a single URL is one page, so prefer the
  // seed — but either one yields the same origin, which is all we want.
  const candidate = config.seed_urls?.[0] || config.single_urls?.[0] || '';
  return normalizeWebsiteUrl(candidate);
};

export const useSuggestedWebsiteUrl = (config?: WebWidgetConfig): string => {
  const accountId = useSelector(selectAccountId);
  const canReadCollections = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.READ)
  );

  const fromDomains = normalizeWebsiteUrl((config?.domains ?? [])[0] ?? '');

  /*
    Both queries reuse the keys the collections module already uses, so an
    author who has visited Knowledge pays nothing here, and one who has not
    warms a cache that page will reuse.

    `canReadCollections` is not decoration: seeing the webchat settings does not
    imply collections access, and without the guard this would fire a request
    that can only 403. A prefill is not worth an error toast.
  */
  const { data: collections } = useQuery<Collections, Error>({
    queryKey: [collectionsEndpoints.collections],
    queryFn: () => collectionsAPI.listAllCollections(),
    enabled: !fromDomains && !!accountId && canReadCollections,
    staleTime: 2 * 60 * 1000,
  });

  const collectionId = collectionWithWebsite(collections);

  const { data: datasources } = useQuery({
    queryKey: [datasourceEndpoints.datasources(collectionId ?? '')],
    queryFn: () => datasourcesAPI.listDatasources(collectionId ?? ''),
    enabled: !fromDomains && !!collectionId && canReadCollections,
    staleTime: 2 * 60 * 1000,
  });

  if (fromDomains) {
    return fromDomains;
  }

  const website = datasources?.datasources?.find(
    (datasource) => datasource.type === DatasourceType.WEBSITE
  );

  return website ? originOfFirstSeed(website.config as WebsiteConfig) : '';
};
